import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  MenuItem,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import ExtensionIcon from '@mui/icons-material/Extension';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useMe } from '../api/hooks.js';
import { useAuth } from '../auth/AuthContext.js';
import { useT } from '../i18n/useT.js';

/**
 * The screen where a person actually decides.
 *
 * Dynamic client registration means anyone can register a client, so nothing
 * before this point grants anything — this is the only place access is given.
 * That shapes what it shows: who is asking, which organization they would see,
 * what they could do with it, and what they could not. A consent screen that
 * only says "an app wants access" is a button people press without reading.
 */
export function OAuthConsentPage() {
  const t = useT().oauth;
  const [params] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { data: me } = useMe();
  const [organizationId, setOrganizationId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => Object.fromEntries(params.entries()), [params]);
  const orgs = me?.organizations ?? [];

  useEffect(() => {
    if (!organizationId && orgs[0]) setOrganizationId(orgs[0].id);
  }, [orgs, organizationId]);

  const details = useQuery({
    enabled: isAuthenticated && !!query.client_id,
    queryKey: ['oauth-consent', query.client_id, query.redirect_uri],
    queryFn: async () =>
      (await api.get(`/oauth/authorize/details?${params.toString()}`)).data as {
        clientName: string;
        clientUri: string | null;
        scope: string;
      },
  });

  const approve = useMutation({
    mutationFn: async () =>
      (await api.post('/oauth/authorize', { ...query, organizationId })).data as { redirectTo: string },
    onSuccess: (d) => {
      // A full navigation, not a router push: the destination belongs to the
      // client, not to this app.
      window.location.assign(d.redirectTo);
    },
    onError: (e) => setError(errorOf(e) ?? t.failed),
  });

  // Sign in first, then come back to exactly this request.
  if (!isAuthenticated) {
    return <Navigate to={`/login?next=${encodeURIComponent(`/oauth/consent?${params.toString()}`)}`} replace />;
  }

  const denied = () => {
    // Refusal goes back to the client as an OAuth error, which is what lets it
    // say "you declined" instead of hanging on a callback that never arrives.
    const uri = query.redirect_uri;
    if (!uri) return;
    const url = new URL(uri);
    url.searchParams.set('error', 'access_denied');
    if (query.state) url.searchParams.set('state', query.state);
    window.location.assign(url.toString());
  };

  const problem = details.error ? (errorOf(details.error) ?? t.invalidRequest) : null;

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, bgcolor: 'background.default' }}>
      <Paper sx={{ p: 4, maxWidth: 520, width: '100%' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <ExtensionIcon color="primary" />
          <Typography variant="h6">{t.title}</Typography>
        </Stack>

        {problem ? (
          <Alert severity="error">{problem}</Alert>
        ) : details.isLoading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <b>{details.data?.clientName}</b> {t.wantsAccess}
            </Typography>
            {details.data?.clientUri && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {details.data.clientUri}
              </Typography>
            )}

            <TextField
              select
              fullWidth
              size="small"
              label={t.organization}
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              sx={{ my: 2 }}
            >
              {orgs.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </TextField>

            <Divider sx={{ my: 1 }} />
            <List dense disablePadding>
              {[t.canRead, t.canAct, t.cannotExceed].map((line) => (
                <ListItem key={line} disableGutters>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <CheckIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={line} />
                </ListItem>
              ))}
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <BlockIcon fontSize="small" color="disabled" />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  primary={t.cannotSignIn}
                />
              </ListItem>
            </List>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {t.revokeHint}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 3 }} justifyContent="flex-end">
              <Button onClick={denied} disabled={approve.isPending}>
                {t.deny}
              </Button>
              <Button
                variant="contained"
                onClick={() => approve.mutate()}
                disabled={approve.isPending || !organizationId}
              >
                {approve.isPending ? t.approving : t.approve}
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    </Box>
  );
}

function errorOf(e: unknown): string | null {
  const data = (e as { response?: { data?: { error_description?: string } } })?.response?.data;
  return data?.error_description ?? null;
}
